/* eslint-disable */

import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import Modal from "@mui/material/Modal";
import moment from "moment";
import React, { useEffect, useMemo, useState } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { clearCreateCoupon } from "../../../actions/cities";
import { CONVERT_TYPE, couponType } from "../../../constants/const";
import { strings } from "../../../I18n";
import { isSuperAdmin } from "../../../utils/utils";
import ButtonWithLoader from "../../ButtonWithLoader";
import InputController from "../../inputController";
import DatePickerController from "../../inputController/DatePickerController";
import { FormInputDropdown } from "../../inputController/FormInputDropdown";
import SwitchController from "../../inputController/SwitchController";

const CouponModal = ({
  onSubmit,
  addModalVisible,
  handleClose,
  isEdit,
  setIsEdit,
  setAddModalVisible,
}) => {
  const style = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 550,
    bgcolor: "background.paper",
    border: "2px solid #000",
    boxShadow: 12,
    p: 4,
    overflowY: "auto",
    maxHeight: "90vh",
    "&::-webkit-scrollbar": {
      WebkitAppearance: "none",
    },
    "&::-webkit-scrollbar-thumb": {
      borderRadius: "8px",
      border: "1px solid white",
      backgroundColor: "rgba(0,0,0,.5)",
    },
    "&::-webkit-scrollbar:vertical": {
      width: "8px",
    },
  };

  const [cityToWhitelabelMapping, setCityToWhitelabelMapping] = useState(null);
  const [selectedCities, setSelectedCities] = useState([]);
  const [selectAllCities, setSelectAllCities] = useState(false);

  const couponByIdSuccess = useSelector(
    (state) => state.cities.couponByIdSuccess
  );
  const couponById = useSelector((state) => state.cities.couponById);
  const whiteLabels = useSelector((state) => state.cities.whiteLabels);
  const adminDetails = useSelector((state) => state.auth.adminDetails);
  const cityWhitelabelMapping = useSelector(
    (state) => state.cities.cityWhitelabelMapping
  );
  const citiesList = useSelector((state) => state.cities.citiesList);
  const createCouponSuccess = useSelector(
    (state) => state.cities.createCouponSuccess
  );

  const remappedWhiteLabels = useMemo(() => {
    if (
      !whiteLabels ||
      !Array.isArray(whiteLabels) ||
      whiteLabels.length === 0
    ) {
      return [];
    }

    let remappedValues = whiteLabels.map((whiteLabel) => {
      return {
        value: whiteLabel.id,
        label: whiteLabel.name,
      };
    });

    remappedValues = remappedValues.map((whiteLabel) => {
      if (whiteLabel.value === null) {
        whiteLabel.value = 10;
      }
      return whiteLabel;
    });

    return remappedValues;
  }, [whiteLabels]);

  const {
    register,
    unregister,
    handleSubmit,
    reset,
    watch,
    control,
    formState: { errors },
    setValue,
    getValues,
  } = useForm({
    defaultValues: {
      validFrom: moment(),
      expiredAT: moment(),
      type: couponType[0].value,
      whiteLabelId: "",
      walletCreditAmount: "",
    },
  });
  const whiteLabelIdWatch = watch("whiteLabelId");
  const watchType = watch("type");
  const watchForSingleUser = watch("forSingleUser");
  const watchNumberOfOccurrences = watch("numberOfOccurrences");
  const watchDurationOfFreeRide = watch("durationOfFreeRide");
  const watchWalletCreditAmount = watch("walletCreditAmount");

  // For non-super-admins: scope cities to their operator assignment.
  // Check cityId first (city operator), then whiteLabelId (whitelabel operator),
  // matching the priority order used in fleets.ts.
  const operatorCities = useMemo(() => {
    const cities = (citiesList || []).map((c) => ({
      value: c.id,
      label: c.name,
    }));
    if (adminDetails?.cityId) {
      return cities.filter((city) => city.value === adminDetails.cityId);
    }
    const wlId = adminDetails?.whiteLabelId;
    if (!wlId || !cityWhitelabelMapping) return cities;
    const wl = cityWhitelabelMapping.find((w) => w.id === wlId);
    if (!wl?.cities) return cities;
    const allowedCityIds = new Set(wl.cities.map((c) => c.id));
    return cities.filter((city) => allowedCityIds.has(city.value));
  }, [cityWhitelabelMapping, adminDetails, citiesList]);

  const getAllCityIdsForWhiteLabel = () => {
    const availableCities = isSuperAdmin(adminDetails)
      ? cityToWhitelabelMapping
      : operatorCities;
    return availableCities?.map((city) => city.value) || [];
  };

  useEffect(() => {
    const realId = whiteLabelIdWatch === 10 ? null : whiteLabelIdWatch;
    const whitelabel = cityWhitelabelMapping?.find(
      (mapping) => mapping.id === realId
    );
    if (cityWhitelabelMapping) {
      setCityToWhitelabelMapping(() => {
        return whitelabel?.cities?.map((city) => {
          return {
            value: city.id,
            label: city.name,
          };
        });
      });
    }
  }, [whiteLabelIdWatch]);
  const dispatch = useDispatch();

  useEffect(() => {
    if (couponByIdSuccess === true) {
      setIsEdit(true);
      dispatch(clearCreateCoupon());
      setAddModalVisible(true);

      const applicableCityIds = couponById?.applicableCityIds || [
        couponById?.cityId,
      ];
      const availableCities = isSuperAdmin(adminDetails)
        ? cityToWhitelabelMapping
        : operatorCities;
      const cities =
        availableCities?.filter((city) =>
          applicableCityIds.includes(city.value)
        ) || [];
      setSelectedCities(cities);
      setSelectAllCities(cities.length === getAllCityIdsForWhiteLabel().length);

      reset({
        applicableCityIds: applicableCityIds,
        code: couponById?.code,
        description: couponById?.description,
        validFrom: couponById?.validFrom
          ? moment(couponById.validFrom)
          : moment(),
        expiredAT: couponById?.expiredAT
          ? moment(couponById.expiredAT)
          : moment(),
        numberOfOccurrences: couponById?.numberOfOccurrences,
        durationOfFreeRide: couponById?.durationOfFreeRide,
        forSingleUser: couponById?.forSingleUser,
        ...couponById,
        whiteLabelId: couponById?.whiteLabelId ?? "",
        walletCreditAmount: couponById?.walletCreditAmount
          ? couponById.walletCreditAmount / 100
          : "",
      });
    }
  }, [couponByIdSuccess]);

  useEffect(() => {
    if (
      isEdit &&
      selectedCities.length === 0 &&
      cityToWhitelabelMapping?.length > 0
    ) {
      const applicableCityIds = getValues("applicableCityIds");
      if (applicableCityIds?.length > 0) {
        const availableCities = isSuperAdmin(adminDetails)
          ? cityToWhitelabelMapping
          : operatorCities;
        const cities =
          availableCities?.filter((city) =>
            applicableCityIds.includes(city.value)
          ) || [];
        setSelectedCities(cities);
        setSelectAllCities(
          cities.length === getAllCityIdsForWhiteLabel().length
        );
      }
    }
  }, [cityToWhitelabelMapping, isEdit]);

  const watchValidFrom = watch("validFrom");

  const watchExpiredAT = watch("expiredAT");
  const watchAutoGenerate = watch("autoGenerate");

  useEffect(() => {
    if (!addModalVisible) {
      setSelectedCities([]);
      setSelectAllCities(false);
      reset({
        applicableCityIds: [],
        code: "",
        description: "",
        validFrom: moment(),
        expiredAT: moment(),
        numberOfOccurrences: "",
        durationOfFreeRide: "",
        forSingleUser: "",
        type: couponType[0].value,
        walletCreditAmount: "",
      });
    }
  }, [addModalVisible]);

  useEffect(() => {
    if (watchAutoGenerate) {
      register("numberOfCoupon");
      register("prefix");
      unregister("code");
    } else {
      unregister("numberOfCoupon");
      unregister("prefix");
      register("code");
    }
  }, [watchAutoGenerate]);

  useEffect(() => {
    if (watchType === "ONE_TIME") {
      setValue("numberOfOccurrences", 1);
    }
    if (watchType === "WALLET_CREDIT" || watchType === "CITY_ACCESS") {
      setValue("durationOfFreeRide", 0);
      setValue("numberOfOccurrences", 0);
    }
  }, [watchType]);

  return (
    <Modal
      open={addModalVisible}
      onClose={handleClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Box sx={style}>
        <FormProvider {...control}>
          <form onSubmit={handleSubmit(onSubmit)} style={{ width: "100%" }}>
            <Typography
              variant="h6"
              sx={{ mb: 2, fontWeight: 600, color: "primary.main" }}
            >
              Basic Information
            </Typography>

            {isSuperAdmin(adminDetails) && (
              <Box sx={{ mb: 1.5 }}>
                <FormInputDropdown
                  name="whiteLabelId"
                  options={remappedWhiteLabels}
                  errors={errors}
                  label={"Select Whitelabel"}
                  required={true}
                  styles={{
                    width: "100%",
                  }}
                  disabled={isEdit}
                />
              </Box>
            )}

            <Box sx={{ mb: 1.5 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={selectAllCities}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setSelectAllCities(checked);
                      if (checked) {
                        const allIds = getAllCityIdsForWhiteLabel();
                        const availableCities = isSuperAdmin(adminDetails)
                          ? cityToWhitelabelMapping
                          : operatorCities;
                        setValue("applicableCityIds", allIds, {
                          shouldValidate: true,
                        });
                        setSelectedCities(availableCities || []);
                      } else {
                        setValue("applicableCityIds", [], {
                          shouldValidate: true,
                        });
                        setSelectedCities([]);
                      }
                    }}
                    disabled={isEdit}
                  />
                }
                label="All Cities"
                sx={{ mb: 1, display: "block" }}
              />
              <Controller
                name="applicableCityIds"
                control={control}
                rules={{
                  required: "At least one city is required",
                  validate: (value) =>
                    (value && value.length > 0) ||
                    "At least one city is required",
                }}
                render={({ field }) => (
                  <Autocomplete
                    multiple={true}
                    disableCloseOnSelect={true}
                    options={
                      isSuperAdmin(adminDetails)
                        ? cityToWhitelabelMapping || []
                        : operatorCities
                    }
                    value={selectedCities}
                    onChange={(event, newValue) => {
                      setSelectedCities(newValue);
                      const cityIds = newValue.map((c) => c.value);
                      field.onChange(cityIds);
                      const allIds = getAllCityIdsForWhiteLabel();
                      setSelectAllCities(
                        newValue.length === allIds.length && allIds.length > 0
                      );
                    }}
                    getOptionLabel={(option) => option.label || ""}
                    isOptionEqualToValue={(option, value) =>
                      option.value === value.value
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={strings("coupon.selectCity")}
                        error={!!errors.applicableCityIds}
                        helperText={
                          errors.applicableCityIds?.message ||
                          `${selectedCities.length} cities selected`
                        }
                      />
                    )}
                    renderOption={(props, option, { selected }) => (
                      <li {...props}>
                        <Checkbox checked={selected} sx={{ mr: 1 }} />
                        {option.label}
                      </li>
                    )}
                    disabled={isEdit}
                    sx={{ width: "100%" }}
                  />
                )}
              />
            </Box>

            <Typography
              variant="h6"
              sx={{ mt: 3, mb: 2, fontWeight: 600, color: "primary.main" }}
            >
              Coupon Type & Configuration
            </Typography>

            <Box sx={{ mb: 1.5 }}>
              <FormInputDropdown
                name="type"
                options={couponType}
                errors={errors}
                label={"Select Coupon Type"}
                required={true}
                disabled={isEdit}
                styles={{
                  width: "100%",
                }}
                control={control}
              />
              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  mt: 0.5,
                  color: "text.secondary",
                  lineHeight: 1.4,
                }}
              >
                <strong>ONE_TIME:</strong> Single-use coupon.{" "}
                <strong>RECURRENT:</strong> Multiple separate rides (e.g., 3
                rides of 20 min each). <strong>DYNAMIC:</strong> Pooled minutes
                used flexibly (e.g., 60 min total, use 10 + 50).{" "}
                <strong>WALLET_CREDIT:</strong> One-time wallet credit. Adds
                funds to user's wallet on redemption.{" "}
                <strong>CITY_ACCESS:</strong> Grants access to private cities on
                redemption.
              </Typography>
            </Box>

            <Box sx={{ mb: 1.5 }}>
              <SwitchController
                control={control}
                name="autoGenerate"
                errors={errors}
                label={"Auto generate coupons"}
                disabled={isEdit}
              />
            </Box>

            {watchAutoGenerate && (
              <>
                <Box sx={{ mb: 1.5 }}>
                  <InputController
                    name={"numberOfCoupon"}
                    label={"Number Of Coupon"}
                    required={!!watchAutoGenerate}
                    disabled={isEdit}
                    styles={{
                      width: "100%",
                    }}
                    extraRules={{
                      min: {
                        value: 1,
                        message: "Minimum value is 1",
                      },
                    }}
                    muiProps={{
                      type: "number",
                    }}
                  />
                </Box>
                <Box sx={{ mb: 1.5 }}>
                  <InputController
                    name={"prefix"}
                    label={"Coupon code prefix"}
                    required={!!watchAutoGenerate}
                    disabled={isEdit}
                    styles={{
                      width: "100%",
                    }}
                  />
                </Box>
              </>
            )}

            {!watchAutoGenerate && (
              <Box sx={{ mb: 1.5 }}>
                <InputController
                  name={"code"}
                  label={strings("coupon.couponCode")}
                  required={true}
                  disabled={isEdit}
                  styles={{
                    width: "100%",
                  }}
                />
              </Box>
            )}

            <Typography
              variant="h6"
              sx={{ mt: 3, mb: 2, fontWeight: 600, color: "primary.main" }}
            >
              Benefit Settings
            </Typography>

            <Box sx={{ mb: 1.5 }}>
              <InputController
                name={"description"}
                label={strings("coupon.description")}
                required={true}
                disabled={isEdit}
                styles={{
                  width: "100%",
                }}
              />
            </Box>

            {watchType !== "WALLET_CREDIT" && watchType !== "CITY_ACCESS" && (
              <>
                <Box sx={{ mb: 1.5 }}>
                  <InputController
                    name={"numberOfOccurrences"}
                    label={strings("coupon.freeRideNumber")}
                    required={true}
                    disabled={isEdit}
                    styles={{
                      width: "100%",
                    }}
                    extraRules={{
                      min: {
                        value: 1,
                        message: "Minimum value is 1",
                      },
                    }}
                    muiProps={{
                      type: "number",
                      disabled: isEdit || watchType === "ONE_TIME",
                    }}
                  />
                  {watchType === "ONE_TIME" && (
                    <Typography
                      variant="caption"
                      sx={{
                        display: "block",
                        mt: 0.5,
                        color: "text.secondary",
                      }}
                    >
                      Single-use coupons always have 1 occurrence
                    </Typography>
                  )}
                  {watchType === "RECURRENT" && (
                    <Typography
                      variant="caption"
                      sx={{
                        display: "block",
                        mt: 0.5,
                        color: "text.secondary",
                      }}
                    >
                      Number of separate rides (e.g., 3 means user gets 3
                      separate rides)
                    </Typography>
                  )}
                  {watchType === "DYNAMIC" && (
                    <Typography
                      variant="caption"
                      sx={{
                        display: "block",
                        mt: 0.5,
                        color: "text.secondary",
                      }}
                    >
                      Multiplier for total pooled minutes (e.g., 3 × 20 min = 60
                      min pool)
                    </Typography>
                  )}
                </Box>

                <Box sx={{ mb: 1.5 }}>
                  <InputController
                    name={"durationOfFreeRide"}
                    label={strings("coupon.freeRideDuration")}
                    required={true}
                    disabled={isEdit}
                    styles={{
                      width: "100%",
                    }}
                    extraRules={{
                      min: {
                        value: 1,
                        message: "Minimum value is 1",
                      },
                    }}
                    muiProps={{
                      type: "number",
                    }}
                  />
                </Box>
              </>
            )}

            <Box sx={{ mb: 1.5 }}>
              <InputController
                name={"walletCreditAmount"}
                label={"Wallet Credit"}
                required={watchType === "WALLET_CREDIT"}
                disabled={isEdit}
                styles={{
                  width: "100%",
                }}
                extraRules={{
                  min: {
                    value: watchType === "WALLET_CREDIT" ? 0.01 : 0,
                    message:
                      watchType === "WALLET_CREDIT"
                        ? "Minimum value is 0.01"
                        : "Must be 0 or greater",
                  },
                }}
                muiProps={{
                  type: "number",
                }}
              />
              <Typography
                variant="caption"
                sx={{ display: "block", mt: 0.5, color: "text.secondary" }}
              >
                Amount to add to user's wallet in the city's local currency.
                {watchType !== "WALLET_CREDIT" &&
                  " Leave empty for no wallet credit."}
              </Typography>
            </Box>

            <Typography
              variant="h6"
              sx={{ mt: 3, mb: 2, fontWeight: 600, color: "primary.main" }}
            >
              Schedule
            </Typography>

            <Box sx={{ mb: 1.5 }}>
              <DatePickerController
                name={"validFrom"}
                errors={errors}
                required={true}
                label={strings("descriptions.startDate")}
                disablePast={!isEdit}
                disabled={isEdit}
                styles={{
                  width: "100%",
                }}
              />
            </Box>

            <Box sx={{ mb: 1.5 }}>
              <DatePickerController
                name={"expiredAT"}
                errors={errors}
                required={true}
                label={strings("coupon.endDate")}
                disablePast={!isEdit}
                disabled={isEdit}
                styles={{
                  width: "100%",
                }}
                extraRules={{
                  validate: (value) => {
                    if (value) {
                      if (value < watchValidFrom) {
                        return "Expire date must be greater than start date";
                      }
                    }
                  },
                }}
              />
            </Box>

            <Typography
              variant="h6"
              sx={{ mt: 3, mb: 2, fontWeight: 600, color: "primary.main" }}
            >
              Advanced Options
            </Typography>

            <Box sx={{ mb: 1.5 }}>
              <SwitchController
                control={control}
                name="forSingleUser"
                errors={errors}
                label={"Single user coupon"}
                disabled={isEdit}
              />
              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  mt: 0.5,
                  color: "text.secondary",
                  lineHeight: 1.4,
                }}
              >
                <strong>Enabled:</strong> Code is exclusive - only the first
                person can claim it. <strong>Disabled:</strong> Code is
                shareable - each user can use it once, but multiple users can
                use the same code.
              </Typography>
            </Box>

            {watchType === "WALLET_CREDIT" &&
              Number(watchWalletCreditAmount) > 0 && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                    Preview
                  </Typography>
                  <Typography variant="body2">
                    {watchAutoGenerate
                      ? `Creating ${getValues("numberOfCoupon") || "?"} coupons`
                      : "Creating 1 coupon"}
                    {`, each adding ${Number(watchWalletCreditAmount).toFixed(2)} to user's wallet.`}
                  </Typography>
                </Alert>
              )}
            {watchType !== "WALLET_CREDIT" &&
              watchNumberOfOccurrences &&
              watchDurationOfFreeRide &&
              watchType && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                    Preview
                  </Typography>
                  <Typography variant="body2">
                    {watchAutoGenerate
                      ? `Creating ${getValues("numberOfCoupon") || "?"} coupons`
                      : "Creating 1 coupon"}
                    {watchType === "ONE_TIME" &&
                      `, each giving users ${watchDurationOfFreeRide} minutes for a single ride.`}
                    {watchType === "RECURRENT" &&
                      `, each giving users ${watchNumberOfOccurrences} separate rides of ${watchDurationOfFreeRide} minutes each (${watchNumberOfOccurrences * watchDurationOfFreeRide} minutes total).`}
                    {watchType === "DYNAMIC" &&
                      `, each giving users ${watchNumberOfOccurrences * watchDurationOfFreeRide} pooled minutes to use flexibly across multiple rides.`}
                  </Typography>
                  {Number(watchWalletCreditAmount) > 0 && (
                    <Typography
                      variant="body2"
                      sx={{ mt: 1, fontWeight: 500, color: "success.main" }}
                    >
                      + {Number(watchWalletCreditAmount).toFixed(2)} wallet
                      credit
                    </Typography>
                  )}
                </Alert>
              )}

            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 2,
                mt: 3,
              }}
            >
              <Button
                variant="outlined"
                color="secondary"
                onClick={() => {
                  setAddModalVisible(false);
                }}
              >
                {strings("coupon.dismiss")}
              </Button>
              {!isEdit && (
                <ButtonWithLoader
                  text={strings("coupon.add")}
                  isLoading={createCouponSuccess === undefined}
                />
              )}
            </Box>
          </form>
        </FormProvider>
      </Box>
    </Modal>
  );
};

export default CouponModal;
